import React, { useState, useEffect } from "react";
import api from "../api/axios";
import { useTranslation } from "../context/LanguageContext";
import "./AjouterAnimal.css";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { GiCow } from "react-icons/gi";
import { FaCheck, FaTimes } from "react-icons/fa";


export default function AjouterAnimal() {
  const { t } = useTranslation();
  const [origine, setOrigine] = useState('naissance');
  const navigate = useNavigate();
  const [animaux, setAnimaux] = useState([]);
  const [troupeaux, setTroupeaux] = useState([]);
  const [ranches, setRanches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [races, setRaces] = useState([]);
  const [robes, setRobes] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showNewRaceInput, setShowNewRaceInput] = useState(false);
  const [showNewRobeInput, setShowNewRobeInput] = useState(false);
  const [showNewRanchInput, setShowNewRanchInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newRaceName, setNewRaceName] = useState("");
  const [newRobeName, setNewRobeName] = useState("");
  const [newRanchName, setNewRanchName] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    matricule: "",
    sexe: "",
    categorie: "",
    date_naissance: "",
    date_entree: "",
    troupeau: "",
    pere: "",
    mere: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    matricule: false,
    sexe: false,
    categorie: false,
    date_naissance: false,
    date_entree: false,
    troupeau: false,
    pere: false,
    mere: false,
  });


  const [newAnimal, setNewAnimal] = useState({
    matricule: "",
    sexe: "",
    date_naissance: null,
    date_entree: null,
    robe: "",
    race: "",
    categorie: "",
    statut: "actif",
    valeur: 0,
    troupeau: "",
    pere: "",
    mere: "",
    ranch_provenance: "",
  });

  // Charger animaux, troupeaux, ranches et catégories
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [animauxRes, troupeauxRes, ranchesRes, categoriesRes, racesRes, robesRes] = await Promise.all([
          api.get("/api/animaux/"),
          api.get("/api/troupeaux/"),
          api.get("/api/ranches/"),
          api.get("/api/categories/"),
          api.get("/api/races/"),
          api.get("/api/robes/"),
        ]);
        setAnimaux(animauxRes.data);
        setTroupeaux(troupeauxRes.data);
        setRanches(ranchesRes.data);
        setCategories(categoriesRes.data);
        setRaces(racesRes.data);
        setRobes(robesRes.data);
      } catch (err) {
        console.error("Erreur lors du chargement :", err);
      }
    };
    fetchData();
  }, []);

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  // Validation d'un champ spécifique
  const validateField = (name, value) => {
    // Champs toujours obligatoires
    if (name === "matricule" || name === "sexe" || name === "categorie" || name === "troupeau") {
      return validateRequiredField(name, value);
    }
    
    // Champs conditionnels selon l'origine
    if (origine === 'naissance') {
      if (name === "date_naissance" || name === "pere" || name === "mere") {
        return validateRequiredField(name, value);
      }
    } else if (origine === 'achat') {
      if (name === "date_entree") {
        return validateRequiredField(name, value);
      }
    }
    
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Convertir les valeurs en nombres pour les clés étrangères
    if (name === "troupeau" || name === "pere" || name === "mere" || name === "ranch_provenance") {
      newValue = value ? Number(value) : null;
    } else if (name === "valeur") {
      newValue = parseFloat(value) || 0;
    } else if (name === "date_naissance" || name === "date_entree") {
      newValue = value || null;
    }
    
    setNewAnimal({ ...newAnimal, [name]: newValue });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, newValue);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let fieldValue = value;
    
    // Convertir la valeur si nécessaire
    if (name === "troupeau" || name === "pere" || name === "mere" || name === "ranch_provenance") {
      fieldValue = value ? Number(value) : null;
    } else if (name === "date_naissance" || name === "date_entree") {
      fieldValue = value || null;
    }
    
    // Marquer le champ comme touché
    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }
    
    // Valider le champ
    const error = validateField(name, fieldValue);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      matricule: true,
      sexe: true,
      categorie: true,
      date_naissance: true,
      date_entree: true,
      troupeau: true,
      pere: true,
      mere: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      matricule: validateField("matricule", newAnimal.matricule),
      sexe: validateField("sexe", newAnimal.sexe),
      categorie: validateField("categorie", newAnimal.categorie || newCategoryName),
      date_naissance: validateField("date_naissance", newAnimal.date_naissance),
      date_entree: validateField("date_entree", newAnimal.date_entree),
      troupeau: validateField("troupeau", newAnimal.troupeau),
      pere: validateField("pere", newAnimal.pere),
      mere: validateField("mere", newAnimal.mere),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      const dataToSend = { ...newAnimal };
      
      // Si une nouvelle catégorie est saisie, utiliser categorie_nom_input
      if (showNewCategoryInput && newCategoryName) {
        dataToSend.categorie_nom_input = newCategoryName;
        delete dataToSend.categorie; // Ne pas envoyer categorie si on crée une nouvelle
      } else if (dataToSend.categorie) {
        // Sinon, utiliser l'ID de la catégorie sélectionnée
        dataToSend.categorie = Number(dataToSend.categorie);
        delete dataToSend.categorie_nom_input;
      } else {
        // Pas de catégorie spécifiée (sera calculée automatiquement si date_naissance existe)
        delete dataToSend.categorie;
        delete dataToSend.categorie_nom_input;
      }

      // Race : ID prioritaire sinon création par nom
      if (showNewRaceInput && newRaceName) {
        dataToSend.race_nom_input = newRaceName;
        delete dataToSend.race;
      } else if (dataToSend.race) {
        dataToSend.race = Number(dataToSend.race);
        delete dataToSend.race_nom_input;
      } else {
        delete dataToSend.race;
        delete dataToSend.race_nom_input;
      }

      // Robe : ID prioritaire sinon création par nom
      if (showNewRobeInput && newRobeName) {
        dataToSend.robe_nom_input = newRobeName;
        delete dataToSend.robe;
      } else if (dataToSend.robe) {
        dataToSend.robe = Number(dataToSend.robe);
        delete dataToSend.robe_nom_input;
      } else {
        delete dataToSend.robe;
        delete dataToSend.robe_nom_input;
      }

      // Ranch de provenance : ID prioritaire sinon création par nom
      if (showNewRanchInput && newRanchName) {
        dataToSend.ranch_provenance_nom_input = newRanchName;
        delete dataToSend.ranch_provenance;
      } else if (dataToSend.ranch_provenance) {
        dataToSend.ranch_provenance = Number(dataToSend.ranch_provenance);
        delete dataToSend.ranch_provenance_nom_input;
      } else {
        delete dataToSend.ranch_provenance;
        delete dataToSend.ranch_provenance_nom_input;
      }
      
      await api.post("/api/animaux/", dataToSend);
      setShowConfirmModal(false);
      toast.success(t.animalAddedSuccess);
      // Navigate after a short delay to allow the toast to be seen
      setTimeout(() => {
        navigate("/animaux");
      }, 500);
    } catch (err) {
      console.error("Erreur backend :", err.response?.data || err.message);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          "Erreur lors de l'ajout de l'animal !";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  // Fonction pour obtenir le nom du troupeau
  const getTroupeauName = (id) => {
    const troupeau = troupeaux.find(t => t.id === id);
    return troupeau ? troupeau.nom : "Non sélectionné";
  };

  // Fonction pour obtenir le matricule du père/mère
  const getParentMatricule = (id) => {
    const parent = animaux.find(a => a.id === id);
    return parent ? parent.matricule : "Non sélectionné";
  };

  // Fonction pour obtenir le nom du ranch
  const getRanchName = (id) => {
    if (!id) return "Non spécifié";
    const ranch = ranches.find(r => r.id === id);
    return ranch ? ranch.nom : "Non spécifié";
  };

  // Vérifier si tous les champs obligatoires sont remplis
  const isFormValid = () => {
    // Champs obligatoires de base
    if (!newAnimal.matricule || !newAnimal.matricule.trim()) {
      return false;
    }
    if (!newAnimal.sexe) {
      return false;
    }
    if (!newAnimal.troupeau) {
      return false;
    }
    
    // Catégorie obligatoire pour naissance et achat
    if (!newAnimal.categorie && !newCategoryName) {
      return false;
    }
    
    // Date selon l'origine
    if (origine === 'naissance' && !newAnimal.date_naissance) {
      return false;
    }
    if (origine === 'achat' && !newAnimal.date_entree) {
      return false;
    }
    
    // Pour une naissance, le geniteur et la genitrice sont obligatoires
    if (origine === 'naissance') {
      if (!newAnimal.pere || !newAnimal.mere) {
        return false;
      }
    }
    
    return true;
  };

  return (
      <div className="ajouter-animal-page animaux-page">
        <div className="title-badge">
          <GiCow className="page-title-icon" aria-hidden="true" />
          <span>{t.addAnimal}</span>
        </div>
        <form onSubmit={handleSubmit} className="animal-form">
          {/* Message d'information sur les champs obligatoires */}
          <div className="form-info-banner">
            <span className="info-icon">ℹ️</span>
            <div className="info-content">
              <strong>{t.requiredFields}</strong>
              <span className="required-indicator">*</span> {t.requiredFieldNote}
              <span> {t.sexAndCategoryRequired}</span>
              {origine === 'naissance' ? (
                <span> {t.birthRequiredFields}</span>
              ) : (
                <span> {t.purchaseRequiredFields}</span>
              )}
            </div>
          </div>
          
          {/* Groupe 1: Informations générales */}
          <div className="form-section-container">
             <div className="form">
             <label>{t.animalOrigin}</label>
          <select value={origine} onChange={(e) => {setOrigine(e.target.value); 
            setNewAnimal({
              ...newAnimal,
              date_naissance: null,
              date_entree: null,
              // Réinitialiser les champs géniteur/genitrice si on passe à "achat"
              pere: e.target.value === 'achat' ? "" : newAnimal.pere,
              mere: e.target.value === 'achat' ? "" : newAnimal.mere,
            });
          
          }}>
            <option value="naissance">{t.birth}</option>
            <option value="achat">{t.purchase}</option>
          </select>
          {/* Affichage conditionnel des champs basés sur l'origine */}
          {origine === 'naissance' ? (
            <>
              <label htmlFor="date_naissance">
                {t.birthDate} <span className="required-indicator">*</span>
              </label>
              <input 
                type="date" 
                id="date_naissance" 
                name="date_naissance" 
                value={newAnimal.date_naissance ?? ""} 
                onChange={handleChange}
                onBlur={handleBlur}
                className={fieldErrors.date_naissance ? "input-error" : ""}
                required 
              />
              {fieldErrors.date_naissance && (
                <span className="field-error-message">{fieldErrors.date_naissance}</span>
              )}
            </>
          ) : (
            <>
              <label htmlFor="date_entree">
                {t.entryDate} <span className="required-indicator">*</span>
              </label>
              <input 
                type="date" 
                id="date_entree" 
                name="date_entree" 
                value={newAnimal.date_entree ?? ""} 
                onChange={handleChange}
                onBlur={handleBlur}
                className={fieldErrors.date_entree ? "input-error" : ""}
                required 
              />
              {fieldErrors.date_entree && (
                <span className="field-error-message">{fieldErrors.date_entree}</span>
              )}
            </>
          )}
          </div>
            <div className="form">
              <label>
                {t.matricule} <span className="required-indicator">*</span>
              </label>
          <input
            type="text"
            name="matricule"
            placeholder={t.matricule}
            value={newAnimal.matricule}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldErrors.matricule ? "input-error" : ""}
            required
          />
          {fieldErrors.matricule && (
            <span className="field-error-message">{fieldErrors.matricule}</span>
          )}
          </div>
          <div className="form">
            <label>
              {t.sex} <span className="required-indicator">*</span>
            </label>
          <select 
            name="sexe" 
            value={newAnimal.sexe} 
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldErrors.sexe ? "input-error" : ""}
            required
          >
            <option value="">{t.selectSex}</option>
            <option value="M">{t.male}</option>
            <option value="F">{t.female}</option>
          </select>
          {fieldErrors.sexe && (
            <span className="field-error-message">{fieldErrors.sexe}</span>
          )}
        </div>
       
          <div className="form">
            <label>{t.breed}</label>
            <select
              name="race"
              value={newAnimal.race || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "new") {
                  setShowNewRaceInput(true);
                  setNewRaceName("");
                  setNewAnimal({ ...newAnimal, race: "", race_nom_input: "" });
                } else {
                  setShowNewRaceInput(false);
                  setNewRaceName("");
                  setNewAnimal({ ...newAnimal, race: value ? Number(value) : "", race_nom_input: "" });
                }
              }}
            >
              <option value="">{t.selectBreed}</option>
              {races.map((race) => (
                <option key={race.id} value={race.id}>
                  {race.nom}
                </option>
              ))}
              <option value="new">{t.createNewBreed}</option>
            </select>
            {showNewRaceInput && (
              <input
                type="text"
                name="race_nom_input"
                placeholder={t.newBreedName}
                value={newRaceName}
                onChange={(e) => {
                  setNewRaceName(e.target.value);
                  setNewAnimal({ ...newAnimal, race_nom_input: e.target.value, race: "" });
                }}
                required
                style={{ marginTop: "8px" }}
              />
            )}
          </div>
          <div className="form">
            <label>{t.coat}</label>
            <select
              name="robe"
              value={newAnimal.robe || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "new") {
                  setShowNewRobeInput(true);
                  setNewRobeName("");
                  setNewAnimal({ ...newAnimal, robe: "", robe_nom_input: "" });
                } else {
                  setShowNewRobeInput(false);
                  setNewRobeName("");
                  setNewAnimal({ ...newAnimal, robe: value ? Number(value) : "", robe_nom_input: "" });
                }
              }}
            >
              <option value="">{t.selectCoat}</option>
              {robes.map((robe) => (
                <option key={robe.id} value={robe.id}>
                  {robe.nom}
                </option>
              ))}
              <option value="new">{t.createNewCoat}</option>
            </select>
            {showNewRobeInput && (
              <input
                type="text"
                name="robe_nom_input"
                placeholder={t.newCoatName}
                value={newRobeName}
                onChange={(e) => {
                  setNewRobeName(e.target.value);
                  setNewAnimal({ ...newAnimal, robe_nom_input: e.target.value, robe: "" });
                }}
                required
                style={{ marginTop: "8px" }}
              />
            )}
          </div>
          <div className="form">
            <label>
              {t.category} <span className="required-indicator">*</span>
            </label>
            <select
              name="categorie"
              value={newAnimal.categorie || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "new") {
                  setShowNewCategoryInput(true);
                  setNewAnimal({ ...newAnimal, categorie: "", categorie_nom_input: "" });
                } else {
                  setShowNewCategoryInput(false);
                  setNewCategoryName("");
                  setNewAnimal({ ...newAnimal, categorie: value ? Number(value) : "", categorie_nom_input: "" });
                }
                // Valider après changement uniquement si une vraie catégorie est sélectionnée
                // Si "new" est sélectionné, on saute la validation et on efface l'erreur existante
                // La validation de newCategoryName se fera quand l'utilisateur tape dans le champ de saisie
                if (value === "new") {
                  // Effacer l'erreur existante quand "new" est sélectionné
                  setFieldErrors(prev => ({
                    ...prev,
                    categorie: ""
                  }));
                } else if (touchedFields.categorie || fieldErrors.categorie) {
                  // Valider seulement si une vraie catégorie est sélectionnée
                  const error = validateField("categorie", value ? Number(value) : "");
                  setFieldErrors(prev => ({
                    ...prev,
                    categorie: error
                  }));
                }
              }}
              onBlur={handleBlur}
              className={fieldErrors.categorie ? "input-error" : ""}
              required={!showNewCategoryInput}
            >
              <option value="">{t.selectCategory}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nom} {cat.sexe && `(${cat.sexe === "M" ? t.male : t.female})`}
                </option>
              ))}
              <option value="new">{t.createNewCategory}</option>
            </select>
            
            {showNewCategoryInput && (
              <input
                type="text"
                name="categorie_nom_input"
                placeholder={t.newCategoryName}
                value={newCategoryName}
                onChange={(e) => {
                  setNewCategoryName(e.target.value);
                  setNewAnimal({ ...newAnimal, categorie_nom_input: e.target.value, categorie: "" });
                  // Valider la nouvelle catégorie
                  if (touchedFields.categorie || fieldErrors.categorie) {
                    const error = validateField("categorie", e.target.value);
                    setFieldErrors(prev => ({
                      ...prev,
                      categorie: error
                    }));
                  }
                }}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, categorie: true }));
                  const error = validateField("categorie", newCategoryName);
                  setFieldErrors(prev => ({ ...prev, categorie: error }));
                }}
                className={fieldErrors.categorie ? "input-error" : ""}
                required
                style={{ marginTop: "8px" }}
              />
            )}
            {fieldErrors.categorie && (
              <span className="field-error-message">{fieldErrors.categorie}</span>
            )}
          </div>
          
          <div className="form">
            <label>{t.estimatedValueLabel}</label>
          <input
            type="number"
            step="0.01"
            name="valeur"
            placeholder={t.estimatedValuePlaceholder}
            value={newAnimal.valeur}
            onChange={handleChange}
          />
          </div>

          {/* Groupe 2: Informations de troupeau et généalogie */}
          <div className="form">
            <label>
              {t.herd} <span className="required-indicator">*</span>
            </label>
          <select
            name="troupeau"
            value={newAnimal.troupeau}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldErrors.troupeau ? "input-error" : ""}
            required
          >
            <option value="">{t.selectHerd}</option>
            {troupeaux.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nom}
              </option>
            ))}
          </select>
          {fieldErrors.troupeau && (
            <span className="field-error-message">{fieldErrors.troupeau}</span>
          )}
          </div>
          
          {/* Afficher les champs géniteur et génitrice uniquement pour les naissances */}
          {origine === 'naissance' && (
            <>
              <div className="form">
                <label>
                  {t.sire} <span className="required-indicator">*</span>
                </label>
              {/* Sélection du père */}
              <div className={fieldErrors.pere && touchedFields.pere ? "select-error-wrapper" : ""}>
                <Select 
                  name="pere" 
                  placeholder={t.selectOrEnterMatricule}
                  value={
                    newAnimal.pere
                      ? (() => {
                          const animal = animaux.find((a) => a.id === newAnimal.pere);
                          if (animal) {
                            const categorieNom = animal.categorie_nom || (animal.categorie && typeof animal.categorie === 'object' ? animal.categorie.nom : null) || (animal.categorie && categories.find(c => c.id === animal.categorie)?.nom) || "";
                            return { 
                              value: newAnimal.pere, 
                              label: categorieNom ? `${animal.matricule} (${categorieNom})` : animal.matricule
                            };
                          }
                          return { value: newAnimal.pere, label: newAnimal.pere };
                        })()
                      : null
                  }
                  onChange={(selectedOption) => {
                    setNewAnimal({
                      ...newAnimal,
                      pere: selectedOption ? selectedOption.value : ""
                    });
                    // Valider après changement
                    if (touchedFields.pere || fieldErrors.pere) {
                      const error = validateField("pere", selectedOption ? selectedOption.value : "");
                      setFieldErrors(prev => ({
                        ...prev,
                        pere: error
                      }));
                    }
                  }}
                  onBlur={() => {
                    setTouchedFields(prev => ({ ...prev, pere: true }));
                    const error = validateField("pere", newAnimal.pere);
                    setFieldErrors(prev => ({ ...prev, pere: error }));
                  }}
                  className={fieldErrors.pere && touchedFields.pere ? "react-select-error" : ""}
                  classNamePrefix="react-select"
                  options={animaux
                    .filter((a) => a.sexe === "M")
                    .map((a) => {
                      const categorieNom = a.categorie_nom || (a.categorie && typeof a.categorie === 'object' ? a.categorie.nom : null) || (a.categorie && categories.find(c => c.id === a.categorie)?.nom) || "";
                      return {
                        value: a.id,
                        label: categorieNom ? `${a.matricule} (${categorieNom})` : a.matricule,
                      };
                    })} 
                />
              </div>
              {fieldErrors.pere && touchedFields.pere && (
                <span className="field-error-message">{fieldErrors.pere}</span>
              )}
                
              </div>
              <div className="form">
                <label>
                  {t.dam} <span className="required-indicator">*</span>
                </label>
              {/* Sélection de la mère */}
              <div className={fieldErrors.mere && touchedFields.mere ? "select-error-wrapper" : ""}>
                <Select 
                  name="mere" 
                  placeholder={t.selectOrEnterMatricule}
                  value={
                    newAnimal.mere
                      ? (() => {
                          const animal = animaux.find((a) => a.id === newAnimal.mere);
                          if (animal) {
                            const categorieNom = animal.categorie_nom || (animal.categorie && typeof animal.categorie === 'object' ? animal.categorie.nom : null) || (animal.categorie && categories.find(c => c.id === animal.categorie)?.nom) || "";
                            return {
                              value: newAnimal.mere,
                              label: categorieNom ? `${animal.matricule} (${categorieNom})` : animal.matricule
                            };
                          }
                          return { value: newAnimal.mere, label: newAnimal.mere };
                        })()
                      : null
                  }
                  onChange={(selectedOption) => {
                    setNewAnimal({
                      ...newAnimal,
                      mere: selectedOption ? selectedOption.value : "", // stocke ID ou texte saisi
                    });
                    // Valider après changement
                    if (touchedFields.mere || fieldErrors.mere) {
                      const error = validateField("mere", selectedOption ? selectedOption.value : "");
                      setFieldErrors(prev => ({
                        ...prev,
                        mere: error
                      }));
                    }
                  }}
                  onBlur={() => {
                    setTouchedFields(prev => ({ ...prev, mere: true }));
                    const error = validateField("mere", newAnimal.mere);
                    setFieldErrors(prev => ({ ...prev, mere: error }));
                  }}
                  className={fieldErrors.mere && touchedFields.mere ? "react-select-error" : ""}
                  classNamePrefix="react-select"
                  options={animaux
                    .filter((a) => a.sexe === "F")
                    .map((a) => {
                      const categorieNom = a.categorie_nom || (a.categorie && typeof a.categorie === 'object' ? a.categorie.nom : null) || (a.categorie && categories.find(c => c.id === a.categorie)?.nom) || "";
                      return {
                        value: a.id,
                        label: categorieNom ? `${a.matricule} (${categorieNom})` : a.matricule,
                      };
                    })} 
                />
              </div>
              {fieldErrors.mere && touchedFields.mere && (
                <span className="field-error-message">{fieldErrors.mere}</span>
              )}
                
              </div>
            </>
          )}

          <div className="form">
            <label>{t.originRanch}</label>
            <select
              name="ranch_provenance"
              value={newAnimal.ranch_provenance || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "new") {
                  setShowNewRanchInput(true);
                  setNewAnimal({ ...newAnimal, ranch_provenance: "", ranch_provenance_nom_input: "" });
                } else {
                  setShowNewRanchInput(false);
                  setNewRanchName("");
                  setNewAnimal({ ...newAnimal, ranch_provenance: value ? Number(value) : "", ranch_provenance_nom_input: "" });
                }
              }}
            >
              <option value="">{t.selectRanch}</option>
              {ranches.map((ranch) => (
                <option key={ranch.id} value={ranch.id}>
                  {ranch.nom} {ranch.code && `(${ranch.code})`}
                </option>
              ))}
              <option value="new">{t.createNewRanch}</option>
            </select>
            
            {showNewRanchInput && (
              <input
                type="text"
                name="ranch_provenance_nom_input"
                placeholder={t.newRanchName}
                value={newRanchName}
                onChange={(e) => {
                  setNewRanchName(e.target.value);
                  setNewAnimal({ ...newAnimal, ranch_provenance_nom_input: e.target.value, ranch_provenance: "" });
                }}
                required
                style={{ marginTop: "8px" }}
              />
            )}
          </div>
              </div>
           {/* Boutons d'action */}
        <div className="actions">
          <button 
            type="submit" 
            className="save-but"
            disabled={!isFormValid()}
          >
            <FaCheck />
            <span>{t.save}</span>
          </button>
          <button 
            type="button"
            onClick={() => navigate("/animaux")} 
            className="cancel-but"
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
        </form>

        {/* Modal de confirmation */}
        {showConfirmModal && (
          <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-modal-header">
                <FaCheck className="confirm-modal-icon" />
                <h3>{t.confirmAdd}</h3>
              </div>
              <div className="confirm-modal-body">
                <p className="confirm-intro">
                  {t.verifyInfo}
                </p>
                <div className="confirm-summary">
                  <div className="summary-row">
                    <span className="summary-label">{t.summaryMatricule}</span>
                    <span className="summary-value">{newAnimal.matricule || t.notProvided}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">{t.summarySex}</span>
                    <span className="summary-value">{newAnimal.sexe === "M" ? t.male : newAnimal.sexe === "F" ? t.female : t.notProvided}</span>
                  </div>
                  {origine === 'naissance' && newAnimal.date_naissance && (
                    <div className="summary-row">
                      <span className="summary-label">{t.summaryBirthDate}</span>
                      <span className="summary-value">{new Date(newAnimal.date_naissance).toLocaleDateString('fr-FR')}</span>
                    </div>
                  )}
                  {origine === 'achat' && newAnimal.date_entree && (
                    <div className="summary-row">
                      <span className="summary-label">{t.summaryEntryDate}</span>
                      <span className="summary-value">{new Date(newAnimal.date_entree).toLocaleDateString('fr-FR')}</span>
                    </div>
                  )}
                  {(newAnimal.race || newRaceName) && (
                    <div className="summary-row">
                      <span className="summary-label">{t.summaryBreed}</span>
                      <span className="summary-value">
                        {showNewRaceInput && newRaceName
                          ? newRaceName
                          : races.find(r => r.id === Number(newAnimal.race))?.nom || newAnimal.race}
                      </span>
                    </div>
                  )}
                  {(newAnimal.robe || newRobeName) && (
                    <div className="summary-row">
                      <span className="summary-label">{t.summaryCoat}</span>
                      <span className="summary-value">
                        {showNewRobeInput && newRobeName
                          ? newRobeName
                          : robes.find(r => r.id === Number(newAnimal.robe))?.nom || newAnimal.robe}
                      </span>
                    </div>
                  )}
                  {(newAnimal.categorie || newCategoryName) && (
                    <div className="summary-row">
                      <span className="summary-label">{t.summaryCategory}</span>
                      <span className="summary-value">
                        {showNewCategoryInput && newCategoryName 
                          ? newCategoryName 
                          : categories.find(c => c.id === Number(newAnimal.categorie))?.nom || newAnimal.categorie}
                      </span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span className="summary-label">{t.summaryStatus}</span>
                    <span className="summary-value">{newAnimal.statut || "Actif"}</span>
                  </div>
                  {newAnimal.valeur > 0 && (
                    <div className="summary-row">
                      <span className="summary-label">{t.summaryValue}</span>
                      <span className="summary-value">{newAnimal.valeur}</span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span className="summary-label">{t.summaryHerd}</span>
                    <span className="summary-value">{getTroupeauName(newAnimal.troupeau)}</span>
                  </div>
                  {newAnimal.pere && (
                    <div className="summary-row">
                      <span className="summary-label">{t.summarySire}</span>
                      <span className="summary-value">{getParentMatricule(newAnimal.pere)}</span>
                    </div>
                  )}
                  {newAnimal.mere && (
                    <div className="summary-row">
                      <span className="summary-label">{t.summaryDam}</span>
                      <span className="summary-value">{getParentMatricule(newAnimal.mere)}</span>
                    </div>
                  )}
                  {(newAnimal.ranch_provenance || newRanchName) && (
                    <div className="summary-row">
                      <span className="summary-label">{t.summaryOriginRanch}</span>
                      <span className="summary-value">
                        {showNewRanchInput && newRanchName
                          ? newRanchName
                          : getRanchName(newAnimal.ranch_provenance)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="confirm-modal-footer">
                <button
                  type="button"
                  onClick={handleCancelConfirm}
                  className="confirm-modal-cancel"
                >
                  <FaTimes />
                  <span>{t.cancel}</span>
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSubmit}
                  className="confirm-modal-confirm"
                >
                  <FaCheck />
                  <span>{t.confirmAdd}</span>
                </button>
              </div>
            </div>
          </div>
        )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      </div>
  );
}
